import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { Toaster } from 'sonner';
import '@/App.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PartnerAuthProvider, usePartnerAuth } from './contexts/PartnerAuthContext';
import PWAInstallPrompt from './components/PWAInstallPrompt';

// Pages
import Landing from './pages/Landing';
import Login from './pages/Login';
import LandingPage from './pages/LandingPage';
import PublicResultChecker from './pages/PublicResultChecker';
import Register from './pages/Register';
import AdminLayout from './components/layouts/AdminLayout';
import MasterDashboard from './pages/master/MasterDashboard';
import SchoolsPage from './pages/master/SchoolsPage';
import PlansPage from './pages/master/PlansPage';
import PaymentSettingsPage from './pages/master/PaymentSettingsPage';
import RevenuePage from './pages/master/RevenuePage';
import RolesPage from './pages/master/RolesPage';
import SystemPage from './pages/master/SystemPage';
import SchoolDashboard from './pages/school/SchoolDashboard';
import StudentsPage from './pages/school/StudentsPage';
import TeachersPage from './pages/school/TeachersPage';
import ClassesPage from './pages/school/ClassesPage';
import AttendancePage from './pages/school/AttendancePage';
import FeesPage from './pages/school/FeesPage';
import SettingsPage from './pages/school/SettingsPage';
import SchoolAssignmentsPage from './pages/school/AssignmentsPage';
import ExamsPage from './pages/school/ExamsPage';
import BulkReportPage from './pages/school/BulkReportPage';
import TeacherAssignmentsPage from './pages/school/TeacherAssignmentsPage';
import AcademicsPage from './pages/school/AcademicsPage';
import AnalyticsPage from './pages/school/AnalyticsPage';
import TimetablePage from './pages/school/TimetablePage';
import AnnouncementsPage from './pages/school/AnnouncementsPage';
import SubscriptionPage from './pages/school/SubscriptionPage';
import TeacherDashboard from './pages/teacher/TeacherDashboard';
import TeacherAssignmentsTPage from './pages/teacher/AssignmentsPage';
import LessonNotesPage from './pages/teacher/LessonNotesPage';
import TeacherResultsPage from './pages/teacher/ResultsPage';
import ClassTeacherPage from './pages/teacher/ClassTeacherPage';
import TeacherTimetablePage from './pages/teacher/TimetablePage';
import TeacherAnnouncementsPage from './pages/teacher/AnnouncementsPage';
import TeacherWeakTopicAlertsPage from './pages/teacher/WeakTopicAlertsPage';
import MyStudentsPage from './pages/teacher/MyStudentsPage';
import TeacherLayout from './pages/teacher/TeacherLayout';
import ParentDashboard from './pages/parent/ParentDashboard';
import ParentFeesPage from './pages/parent/FeesPage';
import ParentResultsPage from './pages/parent/ResultsPage';
import ParentAnnouncementsPage from './pages/parent/AnnouncementsPage';
import ParentChildProgressPage from './pages/parent/ChildProgressPage';
import StudentLayout from './pages/student/StudentLayout';
import ParentLayout from './pages/parent/ParentLayout';
import StudentDashboard from './pages/student/StudentDashboard';
import StudentAssignmentsPage from './pages/student/AssignmentsPage';
import StudentAttendancePage from './pages/student/AttendancePage';
import StudentAnnouncementsPage from './pages/student/AnnouncementsPage';
import StudentResultsPage from './pages/student/ResultsPage';
import StudentReportCardPage from './pages/student/ReportCardPage';
import StudentGamesPage from './pages/student/GamesPage';
import StudentQuizPage from './pages/student/QuizZonePage';
import StudentLeaderboardPage from './pages/student/LeaderboardPage';
import StudentExamPrepPage from './pages/student/ExamPrepPage';
import StudentAcademicGrowthPage from './pages/student/AcademicGrowthPage';
import AiAssistant from './pages/ai/AiAssistant';
import PaymentCallback from './pages/PaymentCallback';
import ApplyPage from './pages/Apply';
import ApplySchool from './pages/ApplySchool';
import AdmissionsPage from './pages/school/AdmissionsPage';
import StudentDetailPage from './pages/school/StudentDetailPage';
import TeacherDetailPage from './pages/school/TeacherDetailPage';
import ParentsPage from './pages/school/ParentsPage';
import ParentDetailPage from './pages/school/ParentDetailPage';
import EventsPage from './pages/school/EventsPage';
import AdminLessonNotesPage from './pages/school/LessonNotesPage';
import StudentLessonNotesPage from './pages/student/LessonNotesPage';
import ParentLessonNotesPage from './pages/parent/LessonNotesPage';
import EarlyWarningsPage from './pages/school/EarlyWarningsPage';
import BusinessViewPage from './pages/school/BusinessViewPage';
import AuditLogsPage from './pages/school/AuditLogsPage';
import BehaviorIncidentsPage from './pages/school/BehaviorIncidentsPage';
import SchoolRolesPage from './pages/school/SchoolRolesPage';
import StudentGroupsPage from './pages/school/StudentGroupsPage';
import PromotionPage from './pages/school/PromotionPage';
import PartnersPage from './pages/master/PartnersPage';
import MasterGamesPage from './pages/master/GamesPage';
import MasterCompetitionsPage from './pages/master/CompetitionsPage';
import MasterQuizPage from './pages/master/QuizManagementPage';
import MarketingPage from './pages/master/MarketingPage';
import WhatsAppPage from './pages/master/WhatsAppPage';
import IntegrationsPage from './pages/master/IntegrationsPage';
import MasterSMSPage from './pages/master/SMSPage';
import SchoolSMSPlatformPage from './pages/school/SMSPlatformPage';
import PartnerSMSPage from './pages/partner/PartnerSMSPage';
import SchoolQuizManagementPage from './pages/school/QuizManagementPage';
import StudentAIActivityPage from './pages/school/StudentAIActivityPage';
import SchoolReportsPage from './pages/school/SchoolReportsPage';
import PartnerLayout from './pages/partner/PartnerLayout';
import PartnerLogin from './pages/partner/PartnerLogin';
import PartnerRegister from './pages/partner/PartnerRegister';
import PartnerDashboard from './pages/partner/PartnerDashboard';
import PartnerReferrals from './pages/partner/PartnerReferrals';
import PartnerWallet from './pages/partner/PartnerWallet';
import PartnerProfile from './pages/partner/PartnerProfile';
import PartnerLeads from './pages/partner/PartnerLeads';
import PartnerLeaderboard from './pages/partner/PartnerLeaderboard';
import RefPage from './pages/RefPage';

function PartnerPrivateRoute({ children }) {
  const { partner, loading } = usePartnerAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-10 h-10 rounded-full border-4 border-[#516EFA] border-t-transparent animate-spin" />
      </div>
    );
  }
  if (!partner) return <Navigate to="/reseller/login" replace />;
  return children;
}

function PartnerApp() {
  return (
    <PartnerAuthProvider>
      <Routes>
        <Route index element={<Navigate to="login" replace />} />
        <Route path="login" element={<PartnerLogin />} />
        <Route path="register" element={<PartnerRegister />} />
        <Route element={
          <PartnerPrivateRoute>
            <PartnerLayout />
          </PartnerPrivateRoute>
        }>
          <Route path="dashboard" element={<PartnerDashboard />} />
          <Route path="leads" element={<PartnerLeads />} />
          <Route path="referrals" element={<PartnerReferrals />} />
          <Route path="wallet" element={<PartnerWallet />} />
          <Route path="profile" element={<PartnerProfile />} />
          <Route path="leaderboard" element={<PartnerLeaderboard />} />
          <Route path="sms" element={<PartnerSMSPage />} />
        </Route>
      </Routes>
    </PartnerAuthProvider>
  );
}
function PrivateRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-10 h-10 rounded-full border-4 border-[#516EFA] border-t-transparent animate-spin mx-auto mb-3" />
          <p className="text-slate-500 text-sm font-medium">Loading Eduva...</p>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to={getRolePath(user.role)} replace />;
  }
  return children;
}

// ─── Module Gate ─────────────────────────────────────────────────────────────
function RequireModule({ module: requiredModule, label, children }) {
  const { school } = useAuth();
  const planModules = school?.plan_modules;
  // No plan restriction configured → allow access
  if (!planModules || planModules.length === 0 || planModules.includes(requiredModule)) {
    return children;
  }
  return (
    <div className="flex flex-col items-center justify-center min-h-[70vh] px-6 text-center" data-testid="module-locked-page">
      <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-5">
        <Lock className="w-8 h-8 text-slate-400" />
      </div>
      <h2 className="text-xl font-bold text-slate-800 mb-2">Module Not Available</h2>
      <p className="text-slate-500 text-sm max-w-sm mb-2">
        The <span className="font-semibold text-slate-700">{label || requiredModule}</span> module is not included in your school's current subscription plan.
      </p>
      <p className="text-xs text-slate-400 mb-6">Contact your platform administrator to upgrade your plan and unlock this feature.</p>
      <a href="/school/dashboard"
        className="px-6 py-2.5 rounded-xl bg-[#516EFA] text-white text-sm font-semibold hover:bg-blue-600 transition-colors"
        data-testid="module-locked-dashboard-btn">
        Go to Dashboard
      </a>
    </div>
  );
}

function getRolePath(role) {
  const paths = {
    master_admin: '/master/dashboard',
    school_owner: '/school/dashboard',
    school_admin: '/school/dashboard',
    teacher: '/teacher/dashboard',
    parent: '/parent/dashboard',
    student: '/student/dashboard',
  };
  return paths[role] || '/login';
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#EFF6FF]">
      <div className="w-8 h-8 border-4 border-[#2563eb] border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (user) return <Navigate to={getRolePath(user.role)} replace />;
  return <LandingPage />;
}

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/check-result" element={<PublicResultChecker />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/payment/callback" element={<PaymentCallback />} />
            <Route path="/apply" element={<ApplyPage />} />
            <Route path="/apply/:slug" element={<ApplySchool />} />
            <Route path="/ref/:code" element={<RefPage />} />

            {/* Partner Portal — standalone, own auth */}
            <Route path="/reseller/*" element={<PartnerApp />} />

            {/* Master Admin */}
            <Route path="/master" element={
              <PrivateRoute allowedRoles={['master_admin']}>
                <AdminLayout role="master" />
              </PrivateRoute>
            }>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<MasterDashboard />} />
              <Route path="schools" element={<SchoolsPage />} />
              <Route path="plans" element={<PlansPage />} />
              <Route path="payment-settings" element={<PaymentSettingsPage />} />
              <Route path="revenue" element={<RevenuePage />} />
              <Route path="roles" element={<RolesPage />} />
              <Route path="system" element={<SystemPage />} />
              <Route path="partners" element={<PartnersPage />} />
              <Route path="games" element={<MasterGamesPage />} />
              <Route path="competitions" element={<MasterCompetitionsPage />} />
              <Route path="quiz" element={<MasterQuizPage />} />
              <Route path="ai" element={<AiAssistant />} />
              <Route path="marketing" element={<MarketingPage />} />
              <Route path="whatsapp" element={<WhatsAppPage />} />
              <Route path="integrations" element={<IntegrationsPage />} />
              <Route path="sms" element={<MasterSMSPage />} />
            </Route>

            {/* School Admin */}
            <Route path="/school" element={
              <PrivateRoute allowedRoles={['school_admin', 'school_owner']}>
                <AdminLayout role="school" />
              </PrivateRoute>
            }>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<SchoolDashboard />} />
              <Route path="students" element={<StudentsPage />} />
              <Route path="teachers" element={<TeachersPage />} />
              <Route path="classes" element={<ClassesPage />} />
              <Route path="academics" element={<AcademicsPage />} />
              <Route path="teacher-assignments" element={<TeacherAssignmentsPage />} />
              <Route path="timetable" element={<TimetablePage />} />
              <Route path="announcements" element={<AnnouncementsPage />} />
              <Route path="attendance" element={<AttendancePage />} />
              <Route path="fees" element={<FeesPage />} />
              <Route path="assignments" element={<SchoolAssignmentsPage />} />
              <Route path="exams" element={<ExamsPage />} />
              <Route path="bulk-report" element={<BulkReportPage />} />
              <Route path="admissions" element={<AdmissionsPage />} />
              <Route path="sms" element={<SchoolSMSPlatformPage />} />
              <Route path="students/:studentId" element={<StudentDetailPage />} />
              <Route path="teachers/:teacherId" element={<TeacherDetailPage />} />
              <Route path="parents" element={<ParentsPage />} />
              <Route path="parents/:parentId" element={<ParentDetailPage />} />
              <Route path="events" element={<EventsPage />} />
              <Route path="lesson-notes" element={<AdminLessonNotesPage />} />
              <Route path="analytics" element={<AnalyticsPage />} />
              <Route path="early-warnings" element={<EarlyWarningsPage />} />
              <Route path="business-view" element={<BusinessViewPage />} />
              <Route path="audit-logs" element={<AuditLogsPage />} />
              <Route path="behavior-incidents" element={<BehaviorIncidentsPage />} />
              <Route path="roles" element={<SchoolRolesPage />} />
              <Route path="groups" element={<StudentGroupsPage />} />
              <Route path="promotions" element={<PromotionPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="subscription" element={<SubscriptionPage />} />
              <Route path="quiz-management" element={
                <RequireModule module="quiz_exam" label="Quiz & Exam">
                  <SchoolQuizManagementPage />
                </RequireModule>
              } />
              <Route path="student-ai-activity" element={<StudentAIActivityPage />} />
              <Route path="reports" element={<SchoolReportsPage />} />
              <Route path="ai" element={<AiAssistant />} />
            </Route>

            {/* Teacher */}
            <Route path="/teacher" element={
              <PrivateRoute allowedRoles={['teacher', 'class_teacher']}>
                <TeacherLayout />
              </PrivateRoute>
            }>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<TeacherDashboard />} />
              <Route path="assignments" element={<TeacherAssignmentsTPage />} />
              <Route path="results" element={<TeacherResultsPage />} />
              <Route path="my-class" element={<ClassTeacherPage />} />
              <Route path="my-students" element={<MyStudentsPage />} />
              <Route path="timetable" element={<TeacherTimetablePage />} />
              <Route path="announcements" element={<TeacherAnnouncementsPage />} />
              <Route path="lessons" element={<LessonNotesPage />} />
              <Route path="weak-topic-alerts" element={<TeacherWeakTopicAlertsPage />} />
              <Route path="ai" element={<AiAssistant />} />
              <Route path="events" element={<EventsPage readOnly={true} />} />
            </Route>

            {/* Parent */}
            <Route path="/parent" element={
              <PrivateRoute allowedRoles={['parent']}>
                <ParentLayout />
              </PrivateRoute>
            }>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<ParentDashboard />} />
              <Route path="results" element={<ParentResultsPage />} />
              <Route path="fees" element={<ParentFeesPage />} />
              <Route path="lessons" element={<ParentLessonNotesPage />} />
              <Route path="announcements" element={<ParentAnnouncementsPage />} />
              <Route path="child-progress" element={<ParentChildProgressPage />} />
              <Route path="ai" element={<AiAssistant />} />
              <Route path="events" element={<EventsPage readOnly={true} />} />
            </Route>

            {/* Student */}
            <Route path="/student" element={
              <PrivateRoute allowedRoles={['student']}>
                <StudentLayout />
              </PrivateRoute>
            }>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<StudentDashboard />} />
              <Route path="assignments" element={<StudentAssignmentsPage />} />
              <Route path="attendance" element={<StudentAttendancePage />} />
              <Route path="announcements" element={<StudentAnnouncementsPage />} />
              <Route path="results" element={<StudentResultsPage />} />
              <Route path="report-card" element={<StudentReportCardPage />} />
              <Route path="lessons" element={<StudentLessonNotesPage />} />
              <Route path="games" element={
                <RequireModule module="games" label="Games">
                  <StudentGamesPage />
                </RequireModule>
              } />
              <Route path="quiz" element={
                <RequireModule module="quiz_exam" label="Quiz Zone">
                  <StudentQuizPage />
                </RequireModule>
              } />
              <Route path="leaderboard" element={<StudentLeaderboardPage />} />
              <Route path="exam-prep" element={
                <RequireModule module="quiz_exam" label="Exam Prep">
                  <StudentExamPrepPage />
                </RequireModule>
              } />
              <Route path="academic-growth" element={<StudentAcademicGrowthPage />} />
              <Route path="ai" element={<AiAssistant />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors closeButton />
        <PWAInstallPrompt />
      </div>
    </AuthProvider>
  );
}

export default App;
